import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

// ── S18 — IVC Co-falla por Área (Spec V4 §3.3) ───────────────────────────────
// Leyenda 4 badges grandes + fórmula bajo cada uno (gris)
// Tabla Maestra con columna Alerta (badge semáforo) + columna IVC (Niv.X)
// Fila Total Planta fondo #3E246B texto blanco

const IVC_LEVELS = [
    {
        nivel: 3,
        tag:      'IVC Niv.3',
        label:    'RIESGO CRÍTICO',
        color:    '#E74C3C',
        textColor: '#fff',
        formula:  'IPRA < 65 + IASH < 60 + NPS < 0',
    },
    {
        nivel: 2,
        tag:      'IVC Niv.2',
        label:    'RIESGO ALTO',
        color:    '#E67E22',
        textColor: '#fff',
        formula:  'IPRA < 65 + IASH < 60',
    },
    {
        nivel: 1,
        tag:      'IVC Niv.1',
        label:    'RIESGO MODERADO',
        color:    '#D4AC0D',
        textColor: '#1a1a1a',
        formula:  'IPRA < 70 ó IASH < 65',
    },
    {
        nivel: 0,
        tag:      'Sin IVC',
        label:    'RIESGO BAJO',
        color:    '#62C190',
        textColor: '#fff',
        formula:  'Sin co-falla activa',
    },
];

const getIvcCfg = (nivel) =>
    IVC_LEVELS.find(l => l.nivel === (nivel ?? 0)) ?? IVC_LEVELS[3];

const fmtNps = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '–';
    return n > 0 ? `+${Math.round(n)}` : String(Math.round(n));
};

const getActionLabel = (nivel) => {
    if (nivel >= 3) return 'Esta semana';
    if (nivel >= 2) return 'Esta semana';
    if (nivel === 1) return '30 días';
    return 'Monitoreo';
};

const ManCrossPageIVCByArea = ({ reportData, headerText, pageNumber }) => {
    const payload   = reportData?.payload || {};
    const icpByArea = payload.icp_by_area || {};
    const kpis      = payload.kpis || {};

    const headcountMap = useMemo(() => {
        const map = {};
        (payload?.headcount_by_management_area || []).forEach(e => { map[e.name] = e.headcount; });
        return map;
    }, [payload]);

    // NPS por área desde nps_data.by_area si existe
    const npsByArea = useMemo(() => {
        const map = {};
        (payload?.nps_data?.by_area || []).forEach(r => { map[r.area] = r.nps; });
        return map;
    }, [payload]);

    const rows = useMemo(() =>
        Object.entries(icpByArea)
            .map(([name, d]) => ({
                name,
                ...d,
                n:   headcountMap[name] ?? d.n ?? 0,
                nps: npsByArea[name] ?? null,
            }))
            .sort((a, b) => (b.ivc ?? 0) - (a.ivc ?? 0) || (a.icp ?? 999) - (b.icp ?? 999)),
        [icpByArea, headcountMap, npsByArea]
    );

    const totalIcp  = kpis.icp_global ?? kpis.cross_index ?? null;
    const totalIpra = kpis.ipra_average ?? null;
    const totalIash = kpis.iash_average ?? null;
    const totalNps  = kpis.ipra_nps ?? kpis.nps_global ?? null;
    const totalN    = Object.values(headcountMap).reduce((s, v) => s + v, 0) || kpis.total_employees || 0;
    const totalIvc  = payload.ivc_by_area
        ? Math.max(...Object.values(payload.ivc_by_area).map(v => v.nivel ?? 0), 0)
        : 0;

    const fmt = (v) => v != null ? Math.round(v) : '–';

    // Summary callout: cuántas áreas en co-falla activa
    const activeAreas = useMemo(() =>
        rows.filter(r => (r.ivc ?? 0) >= 2),
        [rows]
    );
    const worstArea = activeAreas.find(r => (r.ivc ?? 0) >= 3) ?? activeAreas[0];

    const calloutText = activeAreas.length > 0
        ? `${activeAreas.length} áreas en co-falla activa — ${worstArea?.name ?? ''} en Nivel ${worstArea?.ivc ?? ''}: el sistema de barreras está colapsand.`
        : 'Sin áreas en co-falla activa. Sistema de barreras operativo.';

    return (
        <div className="mgcr-ivc-area-page">
            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">Co-Falla por Área</h2>
                <p  className="mgcr-dx-subtitle">Vulnerabilidad Compuesta (IVC)</p>
            </div>

            {/* ── Top strip: callout izquierda + 4 badges de leyenda derecha ── */}
            <div className="mgcr-ivc-area-topstrip">
                <div className="mgcr-ivc-area-callout">
                    {calloutText}
                </div>

                <div className="mgcr-ivc-area-legend">
                    {IVC_LEVELS.map(l => (
                        <div
                            key={l.nivel}
                            className="mgcr-ivc-area-legend-badge"
                            style={{ background: l.color }}
                        >
                            <div className="mgcr-ivc-legend-tag" style={{ color: l.textColor }}>
                                {l.nivel === 0
                                    ? <><span className="mgcr-ivc-legend-pre">Sin</span><span className="mgcr-ivc-legend-main">IVC</span></>
                                    : <><span className="mgcr-ivc-legend-pre">IVC</span><span className="mgcr-ivc-legend-main">Niv.{l.nivel}</span></>
                                }
                            </div>
                            <div className="mgcr-ivc-legend-formula" style={{ color: l.textColor }}>
                                {l.formula}
                            </div>
                            <div className="mgcr-ivc-legend-label" style={{ color: l.textColor }}>
                                {l.label}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* ── Tabla Maestra ── */}
            <div className="mgcr-ivc-area-table-label">Tabla Maestra de Planta</div>
            <div className="mgcr-ivc-area-table">
                {/* Header */}
                <div className="mgcr-ivc-area-row mgcr-ivc-area-head">
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-name">Área</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-n">N°</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-score">IPRA</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-score">IASH</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-nps">NPS</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-icp">ICP</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-ivc">IVC</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-alerta">Alerta</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-accion">Acción</div>
                </div>

                {/* Data rows — fondo tinted según nivel IVC */}
                {rows.map((row) => {
                    const ivcCfg = getIvcCfg(row.ivc ?? 0);
                    return (
                        <div
                            key={row.name}
                            className="mgcr-ivc-area-row mgcr-ivc-area-data-row"
                            style={{ background: `${ivcCfg.color}18` }}
                        >
                            <div className="mgcr-ivc-area-cell mgcr-ivc-c-name">{row.name}</div>
                            <div className="mgcr-ivc-area-cell mgcr-ivc-c-n">{row.n || '–'}</div>
                            <div className="mgcr-ivc-area-cell mgcr-ivc-c-score" style={{ color: ivcCfg.color, fontWeight: 700 }}>{fmt(row.ipra)}</div>
                            <div className="mgcr-ivc-area-cell mgcr-ivc-c-score" style={{ color: ivcCfg.color, fontWeight: 700 }}>{fmt(row.iash)}</div>
                            <div className="mgcr-ivc-area-cell mgcr-ivc-c-nps">{fmtNps(row.nps)}</div>
                            <div className="mgcr-ivc-area-cell mgcr-ivc-c-icp">{fmt(row.icp)}</div>
                            <div className="mgcr-ivc-area-cell mgcr-ivc-c-ivc">
                                {(row.ivc ?? 0) > 0
                                    ? `Niv.${row.ivc}`
                                    : '–'
                                }
                            </div>
                            <div className="mgcr-ivc-area-cell mgcr-ivc-c-alerta">
                                <span
                                    className="mgcr-ivc-badge"
                                    style={{
                                        background: ivcCfg.color,
                                        color:      ivcCfg.textColor,
                                    }}
                                >
                                    {ivcCfg.label}
                                </span>
                            </div>
                            <div className="mgcr-ivc-area-cell mgcr-ivc-c-accion">
                                {row.accion || getActionLabel(row.ivc ?? 0)}
                            </div>
                        </div>
                    );
                })}

                {/* Total Planta */}
                <div className="mgcr-ivc-area-row mgcr-ivc-area-total">
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-name">Total Planta</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-n">{totalN || '–'}</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-score">{fmt(totalIpra)}</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-score">{fmt(totalIash)}</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-nps">{fmtNps(totalNps)}</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-icp">{fmt(totalIcp)}</div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-ivc">
                        {totalIvc > 0 ? `Niv.${totalIvc}` : '–'}
                    </div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-alerta">
                        <span className="mgcr-ivc-badge" style={{ background: getIvcCfg(totalIvc).color, color: getIvcCfg(totalIvc).textColor }}>
                            {getIvcCfg(totalIvc).label}
                        </span>
                    </div>
                    <div className="mgcr-ivc-area-cell mgcr-ivc-c-accion">–</div>
                </div>
            </div>

            {/* Footer */}
            <div className="mgcr-insight-footer">
                <strong>Esto implica que:</strong>{' '}
                El IVC Nivel 3 no es una categoría estadística: es la condición en la que los tres sistemas de defensa
                fallan simultáneamente, haciendo que el accidente sea predecible.
            </div>

            <div className="keep-blankspace" />

            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageIVCByArea;
